import React, { FC, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { gsap } from 'gsap/all'
import FormattedHtmlContent from 'src/components/ui/FormattedHtmlContent'
import { directusDownloadUrl } from 'src/helpers/directus'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import Download from 'src/images/svg/download.svg'
import DownloadWhite from 'src/images/svg/download-white.svg'

interface IDownloadListItemElementProps {
  file: {
    id: string
  }
  description: string
  className?: string
}

export interface IDownloadListItemElementData
  extends IDownloadListItemElementProps {
  id: string
}

const DownloadListItemElement: FC<IDownloadListItemElementProps> = ({
  file,
  description,
  className,
}) => {
  const { t } = useTranslation('gui')
  const [isMounted, setIsMounted] = useState(false)
  const cursorRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsMounted(true)
    if (cursorRef.current) {
      gsap.set(cursorRef.current, { xPercent: -50, yPercent: -50 })
    }
  }, [])

  const handleMouseMove = (e: React.MouseEvent) => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return

    if (cursorRef.current) {
      gsap.to(cursorRef.current, {
        x: e.clientX,
        y: e.clientY,
        duration: 0.15,
        ease: 'power2.out',
        overwrite: 'auto',
      })
    }
  }

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return

    if (cursorRef.current) {
      gsap.set(cursorRef.current, { x: e.clientX, y: e.clientY })
      gsap.to(cursorRef.current, {
        scale: 1,
        opacity: 1,
        duration: 0.3,
        ease: 'power2.out',
      })
    }
  }

  const handleMouseLeave = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return

    if (cursorRef.current) {
      gsap.to(cursorRef.current, {
        scale: 0,
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in',
      })
    }
  }

  return (
    <div
      ref={containerRef}
      className={`group relative transition-all duration-300 rounded-xl overflow-hidden ${className}`}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* 1px Div mimicking the border separator */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-secondaryGray500 group-hover:opacity-0 transition-opacity duration-300 z-20"></div>

      {/* Animated Background Fill */}
      <div className="absolute inset-0 bg-red origin-top transform scale-y-0 transition-transform duration-500 ease-out group-hover:scale-y-100 z-10"></div>

      {/* Floating Cursor Circle */}
      {isMounted && typeof document !== 'undefined'
        ? createPortal(
          <div
            ref={cursorRef}
            className="pointer-events-none fixed left-0 top-0 z-50 flex h-24 w-24 items-center justify-center rounded-full bg-white text-black opacity-0 shadow-2xl"
            style={{ transform: 'scale(0)' }}
          >
            <span className="font-palanquin text-xs font-black uppercase tracking-[0.2em] text-center px-4 leading-tight">
              {t('Download')}
            </span>
          </div>,
          document.body
        )
        : null}

      <a
        href={directusDownloadUrl(file.id)}
        rel="noreferrer"
        className="relative z-20 flex items-center py-6 md:py-8 px-6 md:px-10 w-full md:cursor-none"
      >
        <div className="flex-1 transition-all duration-500 ease-out group-hover:translate-x-4">
          <FormattedHtmlContent
            noMargin={true}
            className="text-megaDarkGray dark:text-white font-semibold transition-colors duration-500 group-hover:text-white"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>

        <div className="transition-all duration-500 ease-out group-hover:-translate-x-4 ml-6">
          <div className="relative flex justify-center items-center h-12 w-12 transition-transform duration-500 group-hover:scale-110">
            <img
              src={Download}
              alt={t('Download')}
              className="w-full h-full object-contain transition-all duration-500 opacity-100 group-hover:opacity-0"
            />
            <img
              src={DownloadWhite}
              alt={t('Download')}
              className="absolute inset-0 w-full h-full object-contain transition-all duration-500 opacity-0 group-hover:opacity-100"
            />
          </div>
        </div>
      </a>
    </div>
  )
}

DownloadListItemElement.defaultProps = {
  className: '',
}

export default DownloadListItemElement
